import React from 'react';
import styled from 'styled-components';
import ClusterAnalysisGraph from '../../../../components/ClusterAnalysisGraph/ClusterAnalysisGraph';
import { useParsedEndDate, useParsedStartDate } from '../../../../utils/ParseDate';
import GraphHOC from '../../../../Common/GraphHOC/GraphHOC';
import BarGraph from '../../../../Common/BarGraph/BarGraph';
import TitleWithInfo from '../../../../Common/TitleWithInfo/TitleWithInfo';

const Wrapper = styled.div`
  padding: 30px 0px 0px 0px;
  display: flex;
  flex-direction: column;
  gap: 40px;
  margin: 40px 20px 10px 25px;
  font-family: "Open Sans";
  height: auto;
  width: 97%;
  box-sizing: border-box;
`;

const Container = styled.div`
  min-height: 100px;
  padding: 25px 20px 25px 50px;
  border-radius: 15px;
  border: 1px solid #d9d9d9;
  display: flex;
  flex-direction: column;
`;

const Title = styled.div`
  font-family: Inter;
  font-size: 18px;
  font-weight: 600;
  margin-left: -35px;
  margin-bottom: 15px;
`;

const Title2 = styled.div`
  color: #636363;
  font-family: Inter;
  font-size: 15px;
  font-weight: 500;
  margin-left: -35px;
  margin-bottom: 15px;
`;

const Footer = styled.div`
  color: rgba(0, 0, 0, 0.87);
  font-family: Inter;
  font-size: 11px;
  font-weight: 400;
  text-align: right;
  margin-top: 15px;
`;

const GraphWrapper = styled.div`
  height: 100%;
  flex: 1;
  display: flex;
  align-items: end;
  padding: 20px;
  box-sizing: border-box;
`;

const ClusterAnalysisTab = ({
  cluster_analysis = {},
  startDate = '',
  endDate = '',
  child = [
    {
      id: "4aa",
      name: "Sentiments",
      type: "checkbox",
      value: false,
      display: false
    },
    {
      id: "4ab",
      name: "Emotions",
      type: "checkbox",
      value: false,
      display: false
    }
  ],
  formatValue = 'dd-MM-yyyy'
}) => {

  const formattedDate = useParsedStartDate(false, '', startDate, formatValue);
  const endDateParsed = useParsedEndDate(false, '', endDate, formatValue);

  if (Object.keys(cluster_analysis).length === 0) {
    return <h2 style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '30px' }}>Insufficient Data</h2>;
  }

  return (
    <Wrapper className="pdf-page" style={{
      pageBreakBefore: 'always', pageBreakAfter: 'auto', pageBreakInside: 'avoid'
    }}>
      {
        child?.length > 0 && (child[0]?.value || child[1]?.value) &&
        <Container className="pdf-page" style={{
          pageBreakBefore: 'auto', pageBreakAfter: 'auto', pageBreakInside: 'avoid', marginTop: '20px'
        }}>
          <Title>
            <TitleWithInfo
              title={'PerceptMap'}
              infoText={"A correlation matrix is a table showing correlation coefficients between variables. Each cell in the table shows the correlation between two variables."}
            />
          </Title>
          <Title2 className="pdf-page">Summaries of Sentiments and Emotions</Title2>

          {
            cluster_analysis?.emotion_data?.circle?.length > 0 ? (
              <div className="pdf-page">
                <ul className="pdf-page" style={{ margin: 0, padding: 0,pageBreakInside: 'avoid', breakInside: 'avoid'}}>
                  {cluster_analysis?.emotion_data?.circle?.map((i, index) => (
                    <div
                      className="pdf-page"
                      key={index}
                      style={{
                        borderBottom: ((index === cluster_analysis?.emotion_data?.circle?.length - 1) || cluster_analysis?.emotion_data?.circle?.length <= 1) ? '' : '1px solid #cccccc',
                        marginTop: index === 0 ? '0px' : '30px',
                        pageBreakInside: 'avoid',
                        breakInside: 'avoid',
                        pageBreakBefore:'auto'
                      }}
                    >
                      <div className="pdf-page" style={{ marginTop: '20px',
                        pageBreakInside: 'avoid',
                        breakInside: 'avoid',
                        pageBreakBefore:'auto' }}>
                        <div style={{ listStyleType: 'disc', marginTop: '30px', pageBreakInside: 'avoid' }}>
                          <span style={{
                            display: 'inline-block',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            whiteSpace: 'pre-wrap',
                            hyphens: 'auto',
                            marginTop:'20px',
                            width: '100%',
                          }}>
                            {i?.summary}
                          </span>
                        </div>


                        <div style={{
                          display: 'flex',
                          gap: '10px',
                          marginBottom: '25px',
                          marginTop: '30px',
                          pageBreakInside: 'avoid'
                        }}>
                          <div style={{ width: '100%' }}>
                            <h3>Emotions</h3>
                            <BarGraph
                              data={[
                                { name: 'Neutral', value: i?.neutral || 0, fill: '#FDE3A2' },
                                { name: 'Surprise', value: i?.surprise || 0, fill: '#4FD786' },
                                { name: 'Joy', value: i?.joy || 0, fill: '#E651CE' },
                                { name: 'Fear', value: i?.fear || 0, fill: '#FFB84D' },
                                { name: 'Sadness', value: i?.sadness || 0, fill: '#5C9BDB' },
                                { name: 'Anger', value: i?.anger || 0, fill: '#F14239' },
                                { name: 'Disgust', value: i?.disgust || 0, fill: '#8862D3' },
                              ]}
                              type={'%'}
                              barHeight={'32px'}
                            />
                          </div>
                          <div style={{ width: '100%' }}>
                            <h3>Sentiments</h3>
                            <BarGraph
                              data={[
                                {
                                  name: 'Positive',
                                  value: parseFloat(cluster_analysis?.sentiment_data?.circle?.[index]?.positive_sentiment || 0),
                                  fill: '#36D174'
                                },
                                {
                                  name: 'Negative',
                                  value: parseFloat(cluster_analysis?.sentiment_data?.circle?.[index]?.negative_sentiment || 0),
                                  fill: '#F14239'
                                },
                                {
                                  name: 'Neutral',
                                  value: parseFloat(cluster_analysis?.sentiment_data?.circle?.[index]?.neutral_sentiment || 0),
                                  fill: '#FCDE94'
                                }
                              ]}
                              type={'%'}
                              barHeight={'32px'}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </ul>
              </div>
            ) : (
              <GraphWrapper style={{
                width: '100%',
                textAlign: 'center',
                justifyContent: 'center',
                alignItems: 'center',
                fontFamily: 'Inter'
              }}>
                Insufficient Data
              </GraphWrapper>
            )
          }

          <Footer>
            This data is observed from {formattedDate} to {endDateParsed}
          </Footer>
        </Container>
      }
    </Wrapper>
  );
};

export default ClusterAnalysisTab;
